import { DndContext, DragEndEvent, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Download, Upload, Play, FileDown, Lock, Unlock, Share2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import ComponentPanel from '@/components/ComponentPanel';
import Canvas from '@/components/Canvas';
import PropertiesPanel from '@/components/PropertiesPanel';
import ProjectSettingsDialog from '@/components/ProjectSettings';
import ThemeToggle from '@/components/ThemeToggle';
import PasswordDialog from '@/components/PasswordDialog';
import CommunityProjectsDialog from '@/components/CommunityProjectsDialog';
import { ComponentType, CanvasComponent, Project, ProjectSettings } from '@/types';
import { generateHtmlCssJs, loadProjectFromJson, saveProjectToJson, exportToZip } from '@/lib/projectUtils';
import { validatePassword } from '@/lib/supabase';

export default function App() {
  const [searchParams] = useSearchParams();
  const [components, setComponents] = useState<CanvasComponent[]>([]);
  const [selectedComponent, setSelectedComponent] = useState<CanvasComponent | null>(null);
  const [showCode, setShowCode] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [generatedCode, setGeneratedCode] = useState({ html: '', css: '', js: '' });
  const [showCommunityProjects, setShowCommunityProjects] = useState(false);
  const [settings, setSettings] = useState<ProjectSettings>({
    name: 'FiveM UI Project',
    author: 'Anonymous',
    nickname: 'fivem-ui',
  });
  const [lastAttemptedAction, setLastAttemptedAction] = useState<() => void>(() => () => {});
  const { toast } = useToast();

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: 10,
    },
  });
  
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 250,
      tolerance: 5,
    },
  });

  const sensors = useSensors(mouseSensor, touchSensor);

  useEffect(() => {
    const urlPassword = searchParams.get('key');
    if (urlPassword) {
      handlePasswordSubmit(urlPassword);
      window.history.replaceState({}, '', '/');
    }
  }, [searchParams]);

  const handlePasswordSubmit = async (password: string) => {
    const isValid = await validatePassword(password);
    
    if (isValid) {
      setIsUnlocked(true);
      setShowPasswordDialog(false);
      toast({
        title: "Features Unlocked",
        description: "You now have access to all features.",
      });
      lastAttemptedAction();
    } else {
      toast({
        title: "Incorrect Password",
        description: "Please try again with the correct password.",
        variant: "destructive",
      });
    }
  };

  const requirePassword = (action: () => void) => {
    if (!isUnlocked) {
      setLastAttemptedAction(() => action);
      setShowPasswordDialog(true);
      return;
    }
    action();
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (over && active.data.current) {
      const componentType = active.data.current.type as ComponentType;
      const newComponent: CanvasComponent = {
        id: `${componentType}-${Date.now()}`,
        type: componentType,
        position: { x: 0, y: 0 },
        size: { width: 200, height: 40 },
        properties: {
          text: 'New Component',
          src: '',
          value: '',
          name: `${componentType}${components.length + 1}`,
          isContainer: componentType === ComponentType.PANEL,
          scrollable: componentType === ComponentType.PANEL,
        },
        events: {
          onClick: '',
          onHover: '',
          onDrag: '',
        },
      };
      
      setComponents([...components, newComponent]);
    }
  };

  const handleComponentSelect = (component: CanvasComponent | null) => {
    setSelectedComponent(component);
  };

  const handleComponentUpdate = (updatedComponent: CanvasComponent) => {
    setComponents(components.map(c => 
      c.id === updatedComponent.id ? updatedComponent : c
    ));
    setSelectedComponent(updatedComponent);
  };

  const handleComponentDelete = (componentId: string) => {
    setComponents(components.filter(c => c.id !== componentId));
    if (selectedComponent?.id === componentId) {
      setSelectedComponent(null);
    }
  };

  const handleExport = () => {
    requirePassword(() => {
      const { html, css, js } = generateHtmlCssJs(components, settings);
      setGeneratedCode({ html, css, js });
      setShowCode(true);
    });
  };

  const handleExportZip = async () => {
    requirePassword(async () => {
      try {
        const zipBlob = await exportToZip(components, settings);
        const url = URL.createObjectURL(zipBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${settings.nickname}.zip`;
        a.click();
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Failed to export zip:', error);
      }
    });
  };

  const handlePreview = () => {
    const componentsData = encodeURIComponent(JSON.stringify(components));
    window.open(`/preview?components=${componentsData}`, '_blank');
  };

  const handleSaveProject = () => {
    requirePassword(() => {
      const project: Project = {
        settings,
        components,
      };
      const projectJson = saveProjectToJson(project);
      const blob = new Blob([projectJson], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${settings.nickname}.json`;
      a.click();
      URL.revokeObjectURL(url);
    });
  };

  const handleLoadProject = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      requirePassword(() => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const content = e.target?.result as string;
          const project = loadProjectFromJson(content);
          setComponents(project.components);
          if (project.settings) {
            setSettings(project.settings);
          }
        };
        reader.readAsText(file);
      });
    }
  };

  return (
    <div className="h-screen flex flex-col">
      <header className="border-b p-4 flex justify-between items-center bg-background">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-bold">FiveM NUI Builder</h1>
          <ProjectSettingsDialog settings={settings} onSave={setSettings} />
        </div>
        <div className="flex gap-2">
          <ThemeToggle />
          <Button variant="outline" onClick={() => setShowCommunityProjects(true)}>
            <Share2 className="w-4 h-4 mr-2" />
            Community Projects
          </Button>
          <Button variant="outline" onClick={handlePreview}>
            <Play className="w-4 h-4 mr-2" />
            Preview
          </Button>
          <Button variant="outline" onClick={handleSaveProject}>
            <Download className="w-4 h-4 mr-2" />
            Save Project
          </Button>
          <label className="cursor-pointer">
            <Button variant="outline" asChild>
              <span>
                <Upload className="w-4 h-4 mr-2" />
                Load Project
              </span>
            </Button>
            <input
              type="file"
              className="hidden"
              accept=".json"
              onChange={handleLoadProject}
            />
          </label>
          <Button variant="outline" onClick={handleExportZip}>
            <FileDown className="w-4 h-4 mr-2" />
            Export ZIP
          </Button>
          <Button onClick={handleExport}>View Code</Button>
          <Button
            variant="outline"
            size="icon"
            onClick={() => {
              if (isUnlocked) {
                setIsUnlocked(false);
                toast({
                  title: "Features Locked",
                  description: "Advanced features are now locked.",
                });
              } else {
                setShowPasswordDialog(true);
              }
            }}
          >
            {isUnlocked ? (
              <Unlock className="h-4 w-4" />
            ) : (
              <Lock className="h-4 w-4" />
            )}
          </Button>
        </div>
      </header>

      <div className="flex-1 flex">
        <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
          <ComponentPanel
            components={components}
            onSelect={handleComponentSelect}
            selectedId={selectedComponent?.id}
          />
          <Canvas
            components={components}
            onSelect={handleComponentSelect}
            onUpdate={handleComponentUpdate}
            onDelete={handleComponentDelete}
            settings={settings}
          />
          <PropertiesPanel
            component={selectedComponent}
            onUpdate={handleComponentUpdate}
          />
        </DndContext>
      </div>

      <Dialog open={showCode} onOpenChange={setShowCode}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Generated Code</DialogTitle>
          </DialogHeader>
          <Tabs defaultValue="html" className="w-full">
            <TabsList>
              <TabsTrigger value="html">HTML</TabsTrigger>
              <TabsTrigger value="css">CSS</TabsTrigger>
              <TabsTrigger value="js">JavaScript</TabsTrigger>
            </TabsList>
            <TabsContent value="html">
              <Textarea
                value={generatedCode.html}
                readOnly
                className="min-h-[400px] font-mono"
              />
            </TabsContent>
            <TabsContent value="css">
              <Textarea
                value={generatedCode.css}
                readOnly
                className="min-h-[400px] font-mono"
              />
            </TabsContent>
            <TabsContent value="js">
              <Textarea
                value={generatedCode.js}
                readOnly
                className="min-h-[400px] font-mono"
              />
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      <PasswordDialog
        open={showPasswordDialog}
        onOpenChange={setShowPasswordDialog}
        onSubmit={handlePasswordSubmit}
      />

      <CommunityProjectsDialog
        open={showCommunityProjects}
        onOpenChange={setShowCommunityProjects}
        currentProject={{ settings, components }}
        onProjectSelect={(project) => {
          setComponents(project.components);
          setSettings(project.settings);
        }}
      />
    </div>
  );
}